/**
 * Product Association component
 *
 * @module swatches-selector
 * @version 1.0.0
 * @extends HTMLElement
 */

customElements.component = 'product-association';

if(!customElements.get(customElements.component)) {

    class productAssociation extends HTMLElement {

        /**
         * Product Association selector web component.
         *
         * @constructor
         */
        constructor() {
            super();

            this.collectionHandle = this.dataset.currentVarSku || this.dataset.handle || "";
            // Add a flag to track variant changes
            this.isChangingVariant = false;
            // Simple failsafe timer to reset isChangingVariant flag
            this._failsafeTimer = null;

            if(this.collectionHandle == ""){
                return;
            }

            window.theme.product.loadProduct(this.dataset.handle, true).then(currentProduct => {
                this.loadSwatchesData(this.collectionHandle).then((allProductsColor) => {
                    this.render(currentProduct, allProductsColor);
                });
            });
        }

        /**
         * Reset the failsafe timer
         */
        setupFailsafeTimer() {
            // Clear any existing timer
            if (this._failsafeTimer) {
                clearTimeout(this._failsafeTimer);
            }
            
            // Set a new timer to reset the flag after 2 seconds
            this._failsafeTimer = setTimeout(() => {
                if (this.isChangingVariant) {
                    console.log("Failsafe: Resetting isChangingVariant flag");
                    this.isChangingVariant = false;
                }
            }, 2000);
        }

        /**
         * Load Swatches data
         *
         * @param {string} collectionHandle Collection Handle.
         * @returns {json}
         */
        loadSwatchesData(collectionHandle) {
            return new Promise((resolve, reject) => {
                let url = 'collections/' + collectionHandle + '?view=products-json';

                fetch(window.Shopify.routes.root + url)
                    .then(resp => {
                        // Check if the response is valid JSON
                        const contentType = resp.headers.get('content-type');
                        if (contentType && contentType.includes('application/json')) {
                            return resp.json();
                        } else {
                            console.warn('Non-JSON response from collection fetch, returning empty array');
                            return { products: [] };
                        }
                    })
                    .then(data => {
                        // Validate that we have a products array
                        if (data && Array.isArray(data.products)) {
                            resolve(data.products);
                        } else {
                            console.warn('Invalid products data, returning empty array');
                            resolve([]);
                        }
                    })
                    .catch(err => {
                        // Returns an empty array if the collection does not exist or any error occurs
                        console.error('Error fetching collection data:', err);
                        resolve([]);
                    });
            });
        }

        /**
         * Render component in the DOM
         *
         * @param {object} product Product data.
         * @returns {undefined}
         */
        render(product, allProductsColor) {

            let html = this.getTemplate(),
                content = '';

            this.classList.add('swatches');

            if (html && !this.querySelector('[data-option]')) {

                if (product.options.some(option => option.values.length > 1)) {

                    product.options.forEach((option, index) => {

                        let position = index + 1;

                        content = content + `<div class="swatch-option" data-option="${option.name}" data-position="${position}">`;
                        content = content + `<div class="swatch-label"><span>${option.name}</span></div>`;

                        for (let index = 0; index < (Math.floor(Math.random() * 3) + 2); index++) {
                            content = content + `<div class="swatch swatch-loading"></div>`;
                        }

                        content = content + `</div>`;
                    });

                    this.innerHTML = content;
                }
            }

            this.loadSwatches(product, allProductsColor);

            this.selectFirstVariantAvailable(product);

            this.initEvents(product);

            this.isSizeSelected = this.checkSizeSelected();

            // Dispatch swatches-ready event after rendering
            this.dispatchEvent(new CustomEvent('swatches-ready', { bubbles: true }));
        }

        /**
         * Get component template
         *
         * @returns {string} Component HTML template.
         */
        getTemplate() {

            return '{{content}}';
        }

        /**
         * Init component events.
         *
         * @param {object} product Product data.
         * @returns {undefined}
         */
        initEvents(product) {
            this.isSizeSelected = false;
            // Track which elements already have listeners to avoid duplicates
            this._swatchInputsWithListeners = this._swatchInputsWithListeners || new Set();
            this._variantSelectorsWithListeners = this._variantSelectorsWithListeners || new Set();
            
            // On swatch change
            this.querySelectorAll('[data-swatch-input]').forEach(element => {
                // Skip if this element already has a listener
                if (this._swatchInputsWithListeners.has(element)) {
                    return;
                }
                
                element.addEventListener('change', event => {
                    if(element.name.trim().toLowerCase() === 'size') {
                        this.isSizeSelected = true;
                    }
                    this.onSwatchChange(event.target, product);
                });
                
                // Mark this element as having a listener
                this._swatchInputsWithListeners.add(element);
            });

            // On variant selector ready
            let variantSelector = document.querySelector('variant-selector[data-handle="'+this.dataset.handle+'"]');
            
            // Skip if this variant selector already has listeners
            if (!this._variantSelectorsWithListeners.has(variantSelector)) {
                variantSelector.addEventListener('ready', event => {
                    const select = variantSelector.querySelector('select');
                    
                    // Skip if select already has a listener
                    if (!select._changeListenerAdded) {
                        select.addEventListener('change', event => {
                            this.updateSwatchValues();
                        });
                        select._changeListenerAdded = true;
                    }

                    this.selectFirstVariantAvailable(product);
                    this.disableOutOfStockVariants();
                });

                // After variant selector ready event
                if (variantSelector.classList.contains('ready')) {
                    const select = variantSelector.querySelector('select');
                    
                    // Skip if select already has a listener
                    if (!select._changeListenerAdded) {
                        select.addEventListener('change', event => {
                            this.updateSwatchValues();
                        });
                        select._changeListenerAdded = true;
                    }

                    this.selectFirstVariantAvailable(product);
                    this.disableOutOfStockVariants();
                }
                
                // Mark this variant selector as having listeners
                this._variantSelectorsWithListeners.add(variantSelector);
            }
            
            // Add cart update listener only once
            if (!this._cartUpdateListenerAdded) {
                document.addEventListener('cart:updated', () => {
                    if (!this.isChangingVariant) {
                        const variant = this.getSelectedVariant(product);
                        if (variant && window.productsInventoryQuantity) {
                            this.checkInventoryStatus(variant);
                        }
                    }
                });
                this._cartUpdateListenerAdded = true;
            }
        }

        /**
         * Load swatch inputs from a product
         *
         * @param {object} product Product data.
         * @returns {undefined|boolean}
         */
        loadSwatches(product, allProductsColor) {
            if (!window.theme.product.hasVariants(product))
                return false;

            product.options.forEach((option) => {
                // Use color/image logic only for those specific cases, otherwise always use loadSwatchText
                if (option.name.toUpperCase() === 'COLOR' && window.theme.hex_swatches && window.theme.hex_swatches.display_hex_color_swatches && window.theme.hex_swatches.colors && window.theme.hex_swatches.colors.length > 0) {
                    this.loadSwatchColorHex(option, allProductsColor, product);
                } else if (option.name.toUpperCase() === 'COLOR' && (!window.theme.hex_swatches || !window.theme.hex_swatches.display_hex_color_swatches)) {
                    this.loadSwatchColorText(option, allProductsColor);
                } else if (this.getImageSwatchOptions().includes(option.name.toUpperCase())) {
                    this.loadSwatchImage(option, product);
                } else {
                    // For all other options, always use loadSwatchText (square swatch)
                    this.loadSwatchText(option, product);
                }
            });
        }

        /**
         * Load swatch text color
         *
         * @param {object} option Option data.
         * * @param {list} allProductsColor products
         * @returns {undefined|boolean}
         */
        loadSwatchColorText(option, allProductsColor) {
            let element     = this.getSwatchElement(option.name),
                label       = this.getSwatchLabelTemplate(),
                rand        = Math.floor(Math.random() * 100),
                finalHtml   = '';

            if (element) {

                let additionalClass = (option.values.length == 0) ? 'hidden' : '';

                element.dataset.type = 'text';

                label = label.replaceAll('{{ option_name }}', option.name);

                allProductsColor.forEach( item => {

                    if((item.options_by_name.COLOR) || (item.options_by_name.Color) || (item.options_by_name.color)){

                        let colorOption = item.options_by_name.COLOR || item.options_by_name.Color || item.options_by_name.color;

                        let value = colorOption.option.values[0];

                        let html =
                        `<div class="swatch swatch--text relative {{ additionalClass }}">
                            <input type="radio" id="{{ id }}" name="{{ name }}" value="{{ value }}" aria-label="{{ value }}" data-swatch-input="{{ name }}">
                            <label for="{{ id }}">{{ value }}</label>
                            <a href="{{ link }}" class="absolute top-0 bottom-0 right-0 left-0"></a>
                        </div>`;

                        let id = 'option-' + rand + '-' + option.name.toLowerCase().replaceAll(' ', '-') + '-' + value.toLowerCase().replaceAll(' ', '-');

                        html = html.replaceAll("{{ value }}", value);
                        html = html.replaceAll("{{ name }}", option.name);
                        html = html.replaceAll("{{ id }}", id);
                        html = html.replaceAll("{{ handle }}", item.product.handle);
                        html = html.replaceAll("{{ link }}", `/products/${item.product.handle}` );
                        html = html.replaceAll("{{ additionalClass }}", additionalClass);

                        finalHtml = finalHtml + html;

                    }

                });

                element.innerHTML = label + finalHtml;
                element.innerHTML = label + '<div class="flex-grow flex flex-wrap">' + finalHtml + '</div>';
            }
        }

        /**
         * Load swatch hex color
         *
         * @param {object} option Option data.
         * * @param {list} allProductsColor products
         * @returns {undefined|boolean}
         */
        loadSwatchColorHex(option, allProductsColor, product) {

            let element     = this.getSwatchElement(option.name),
                label       = this.getSwatchLabelTemplate(),
                rand        = Math.floor(Math.random() * 100),
                finalHtml   = '';

            if (element) {

                let additionalClass = (option.values.length == 0) ? 'hidden' : '';

                element.dataset.type = 'color';

                label = label.replaceAll('{{ option_name }}', option.name);

                if(allProductsColor.length > 0) {

                    allProductsColor.forEach( item => {

                        let hideUnavailable = window.theme.hex_swatches.hide_unavailable_product_swatches || false;

                        let displayItem = ((item.product.available) || (hideUnavailable == false));

                        if(displayItem) {

                            if((item.options_by_name.COLOR) || (item.options_by_name.Color) || (item.options_by_name.color)){

                                let colorOption = item.options_by_name.COLOR || item.options_by_name.Color || item.options_by_name.color;

                                let value = colorOption.option.values[0];

                                let handle_color_value = window.theme.handleize(value);

                                let swatchObj = (window.theme.hex_swatches.colors.find( item => {return item.handle_title == handle_color_value})) || "";

                                // Fallback for missing swatchObj
                                if(swatchObj == "") {
                                    swatchObj = {};
                                    let splitArr = handle_color_value.split("-");
                                    if(splitArr.length > 0){
                                        for (let i = 0; i < splitArr.length; i++) {
                                            splitArr[i] = splitArr[i][0].toUpperCase() + splitArr[i].substr(1).toLowerCase();
                                        }
                                        swatchObj.color = splitArr.join(" ").replaceAll(" ", "").trim();
                                    }else{
                                        swatchObj.color = value;
                                    }
                                }

                                let additionalColorClass = (swatchObj.color && (swatchObj.color.toLowerCase() == 'white' || swatchObj.color.toLowerCase() == '#ffffff')) ? 'border-gray-300' : '';

                                let html = '';
                                let id = 'option-' + rand + '-' + option.name.toLowerCase().replaceAll(' ', '-') + '-' + value.toLowerCase().replaceAll(' ', '-');

                                if (swatchObj.image) {
                                    // Render image swatch
                                    html = `<div class="swatch swatch--color-hex swatch--color-image relative mb-2 mr-3 {{ additionalClass }}">
                                        <input title="{{ value }}" type="radio" id="{{ id }}" name="{{ name }}" value="{{ value }}" aria-label="{{ value }}" data-swatch-input="{{ name }}">
                                        <div class="color-hex-item w-8 h-8 border-x border-y border-solid border-grey {{ additionalColorClass }} flex items-center justify-center overflow-hidden">
                                            <img src="{{ image_src }}" alt="{{ value }}" class="object-cover w-full h-full" />
                                        </div>
                                        <a href="{{ link }}" title="{{ value }}" class="absolute top-0 bottom-0 right-0 left-0"></a>
                                    </div>`;
                                    html = html.replaceAll("{{ image_src }}", swatchObj.image);
                                } else {
                                    // Render hex color swatch
                                    html = `<div class="swatch swatch--color-hex relative mb-2 mr-3 {{ additionalClass }}">
                                        <input title="{{ value }}" type="radio" id="{{ id }}" name="{{ name }}" value="{{ value }}" aria-label="{{ value }}" data-swatch-input="{{ name }}">
                                        <div class="color-hex-item w-8 h-8 border-x border-y border-solid border-grey {{ additionalColorClass }}" style="background-color: {{ hex_color }};"></div>
                                        <a href="{{ link }}" title="{{ value }}" class="absolute top-0 bottom-0 right-0 left-0"></a>
                                    </div>`;
                                    html = html.replaceAll("{{ hex_color }}", swatchObj.color );
                                }

                                html = html.replaceAll("{{ value }}", value);
                                html = html.replaceAll("{{ name }}", option.name);
                                html = html.replaceAll("{{ id }}", id);
                                html = html.replaceAll("{{ handle }}", item.product.handle);
                                html = html.replaceAll("{{ link }}", `/products/${item.product.handle}` );
                                html = html.replaceAll("{{ additionalClass }}", additionalClass);
                                html = html.replaceAll("{{ additionalColorClass }}", additionalColorClass);

                                finalHtml = finalHtml + html;
                            }
                        }
                    });

                } else {
                  const sortedProductArray = {};

                  option.values.forEach(value => {
                    const filteredVariants = product.variants.filter(variant => variant.option2 === value);

                    if (filteredVariants.length > 0) {
                      const minPrice = Math.min(...filteredVariants.map(variant => variant.price));

                       if (!sortedProductArray[minPrice]) {
                        sortedProductArray[minPrice] = [];
                       }

                       sortedProductArray[minPrice].push(...filteredVariants);
                    }
                  });

                    const sortedPrices = Object.keys(sortedProductArray).sort((a, b) => a - b);
                    const finalSortedProductArray = [];

                    sortedPrices.forEach(price => {
                      finalSortedProductArray.push(...sortedProductArray[price]);
                    });

                    let prevHtml = '';
                    const sortedProductEntries = Object.entries(sortedProductArray).reverse();

                    sortedProductEntries.forEach(elem => {
                      option.values.forEach(value => {
                        let handle_color_value = window.theme.handleize(value);
                        let swatchObj = (window.theme.hex_swatches.colors.find( item => {return item.handle_title == handle_color_value})) || "";
                        if (swatchObj == "") {
                          swatchObj = {};
                          let splitArr = handle_color_value.split("-");
                          if (splitArr.length > 0) {
                            for (let i = 0; i < splitArr.length; i++) {
                              splitArr[i] = splitArr[i][0].toUpperCase() + splitArr[i].substr(1).toLowerCase();
                            }
                            swatchObj.color = splitArr.join(" ").replaceAll(" ", "").trim();
                          } else {
                            swatchObj.color = value;
                          }
                        }
                        let additionalColorClass = (swatchObj.color && (swatchObj.color.toLowerCase() == 'white' || swatchObj.color.toLowerCase() == '#ffffff')) ? 'border-gray-300' : '';
                        let html = '';
                        let id = 'option-' + rand + '-' + option.name.toLowerCase().replaceAll(' ', '-') + '-' + value.toLowerCase().replaceAll(' ', '-');
                        if (swatchObj.image) {
                          html = `<div class="swatch swatch--color-hex swatch--color-image mb-2 mr-3 relative {{ additionalClass }}">
                            <input class="!block  cursor-pointer absolute w-full h-full rounded-none opacity-0" title="{{ value }}" type="radio" id="{{ id }}" name="{{ name }}" value="{{ value }}" aria-label="{{ value }}" data-swatch-input="{{ name }}">
                            <div class="color-hex-item w-8 h-8 border-x border-y border-solid border-grey {{ additionalColorClass }} flex items-center justify-center overflow-hidden">
                              <img src="{{ image_src }}" alt="{{ value }}" class="object-cover w-full h-full" />
                            </div>
                          </div>`;
                          html = html.replaceAll("{{ image_src }}", swatchObj.image);
                        } else {
                          html = `<div class="swatch lol swatch--color-hex mb-2 mr-3 relative {{ additionalClass }}">
                            <input class="!block  cursor-pointer absolute w-full h-full rounded-none opacity-0" title="{{ value }}" type="radio" id="{{ id }}" name="{{ name }}" value="{{ value }}" aria-label="{{ value }}" data-swatch-input="{{ name }}">
                            <div title="{{ value }}" class="color-hex-item w-8 h-8 border-x border-y border-solid border-grey {{ additionalColorClass }}" style="background-color: {{ hex_color }};"></div>
                          </div>`;
                          html = html.replaceAll("{{ hex_color }}", swatchObj.color );
                        }
                        html = html.replaceAll("{{ value }}", value);
                        html = html.replaceAll("{{ name }}", option.name);
                        html = html.replaceAll("{{ id }}", id);
                        html = html.replaceAll("{{ additionalClass }}", additionalClass);
                        html = html.replaceAll("{{ additionalColorClass }}", additionalColorClass);
                        const filteredVariants = elem[1].filter(variant => variant.option2 === value);
                        if (filteredVariants.length > 0) {
                          prevHtml += html;
                        }
                      });
                    })

                    finalHtml = prevHtml;
                }

                element.innerHTML = label + finalHtml;
                element.innerHTML = label + '<div class="flex-grow flex flex-wrap">' + finalHtml + '</div>';
            }
        }

        /**
         * Get array of the custom option to be used as an image swatch.
         *
         * @returns {Array} Array of the custom options.
         */
        getImageSwatchOptions() {

            if (typeof this.imageSwatchOptions == 'undefined') {

                this.imageSwatchOptions = (this.dataset.imageOptions) ? this.dataset.imageOptions.toUpperCase().split(',') : '';

                return this.imageSwatchOptions;

            } else {

                return this.imageSwatchOptions;
            }
        }

        /**
         * Get swatch element from DOM.
         *
         * @returns {object} Swatch element.
         */
        getSwatchElement(optionName) {

            return this.querySelector('[data-option="' + optionName.replaceAll(' ', '-') + '"]');
        }

        /**
         * Render swatch as an image.
         *
         * @param {object} product Product data.
         * @param {object} option Product option data.
         * @returns {undefined|boolean}
         */
        loadSwatchImage(option, product) {

            let element     = this.getSwatchElement(option.name),
                swatches    = window.theme.swatches.getSwatches(product, option.name),
                label       = this.getSwatchLabelTemplate(),
                rand        = Math.floor(Math.random() * 100),
                finalHtml   = '';

            element.dataset.type = 'image';

            label = label.replaceAll('{{ option_name }}', option.name);

            option.values.forEach(value => {

                let html    = this.getSwatchImageTemplate(),
                    image   = swatches.find(item => {return item.value == value}),
                    id      = 'option-' + rand + '-' + option.name.toLowerCase().replaceAll(' ', '-') + '-' + value.toLowerCase().replaceAll(' ', '-');

                if(!image.src)
                    return false;

                html = html.replaceAll("{{ value }}", value);
                html = html.replaceAll("{{ name }}", option.name);
                html = html.replaceAll("{{ id }}", id);
                html = html.replaceAll("{{ src }}", image.src);

                finalHtml = finalHtml + html;
            });

            element.innerHTML = label + finalHtml;
        }

        /**
         * Render swatch as a text.
         *
         * @todo Merge this fuction with loadSwatchImage.
         * @param {object} product Product data.
         * @param {object} option Product option data.
         * @returns {undefined|boolean}
         */
        loadSwatchText(option, product) {

            let element     = this.getSwatchElement(option.name),
                label       = this.getSwatchLabelTemplate(),
                rand        = Math.floor(Math.random() * 100),
                finalHtml   = '';

            if (element) {

                let additionalClass = (option.values.length == 0) ? 'hidden' : '';

                element.dataset.type = 'text';

                label = label.replaceAll('{{ option_name }}', option.name);

                option.values.forEach(value => {

                    let html    = this.getSwatchTextTemplate(),
                        id      = 'option-' + rand + '-' + option.name.toLowerCase().replaceAll(' ', '-') + '-' + value.toLowerCase().replaceAll(' ', '-');

                    html = html.replaceAll("{{ value }}", value);
                    html = html.replaceAll("{{ name }}", option.name);
                    html = html.replaceAll("{{ id }}", id);
                    html = html.replaceAll("{{ additionalClass }}", additionalClass);

                    finalHtml = finalHtml + html;
                });

                element.innerHTML = label + finalHtml;
                element.innerHTML = label + '<div class="flex-grow flex flex-wrap">' + finalHtml + '</div>';
            }
        }

        /**
         * Get swatch label html template.
         *
         * @todo Move this template to a liquid file.
         * @returns {string} HTML template.
         */
         getSwatchLabelTemplate() {

            return '<div class="swatches__swatch-label">Select <span class="swatches__swatch-title">{{ option_name }}</span>: <strong class="text-sm pb-3" data-swatch-label="{{ option_name }}"></strong></div>';
        }


        /**
         * Get swatch image html template.
         *
         * @todo Move this template to a liquid file.
         * @returns {string} HTML template.
         */
        getSwatchImageTemplate() {

            return '<div class="swatch swatch--image">' +
                        '<input type="radio" id="{{ id }}" name="{{ name }}" value="{{ value }}" aria-label="{{ value }}" data-swatch-input="{{ name }}">' +
                        '<label for="{{ id }}">{{ value }}</label>' +
                        '<img src="{{ src }}" alt="{{ value }}"/>' +
                    '</div>';
        }

        /**
         * Get swatch text html template.
         *
         * @todo Move this template to a liquid file.
         * @returns {string} HTML template.
         */
        getSwatchTextTemplate() {

            return '<div class="swatch swatch--text {{ additionalClass }}">' +
                        '<input type="radio" id="{{ id }}" name="{{ name }}" value="{{ value }}" aria-label="{{ value }}" data-swatch-input="{{ name }}">' +
                        '<label for="{{ id }}">{{ value }}</label>' +
                    '</div>';
        }

        /**
         * Check if size is currently selected
         *
         * @returns {boolean} True if size is selected, false otherwise
         */
        checkSizeSelected() {
            const allOptions = this.querySelectorAll('[data-option]');
            for (let option of allOptions) {
                const optionName = option.getAttribute('data-option');
                if (optionName && optionName.toLowerCase().includes('size')) {
                    const sizeInput = option.querySelector('input[data-swatch-input]:checked');
                    return sizeInput !== null;
                }
            }
            return false;
        }

        /**
         * On swatch change.
         *
         * @param {object} swatchInput Swatch input dom element.
         * @param {object} product Product data.
         * @returns {undefined}
         */
        onSwatchChange(swatchInput, product) {
            // Set flag to prevent inventory checks during variant change
            this.isChangingVariant = false;
            
            // Set up failsafe timer
            this.setupFailsafeTimer();
            
            // Immediately reset UI state
            this.resetInventoryUI();

            let addToShowroomButton = null;

            if (this.closest("component-quickview")) {
                addToShowroomButton = this.closest("component-quickview").querySelector(".js-add-to-showroom-btn");
            } else if (this.closest(".product-grid__item")) {
                this.closest(".product-grid__item").querySelector(".js-add-to-showroom-btn");
            }

            this.updateOptionLabel(swatchInput);
            
            this.isSizeSelected = this.checkSizeSelected();
            
            let variant = this.getSelectedVariant(product);
            this.updateVariantSelector(variant);
            this.updateLineProperty(variant);
            this.disableOutOfStockVariants();

            addToShowroomButton && addToShowroomButton.setAttribute("data-color-name", swatchInput.value);

            // Use a shorter delay to ensure all DOM updates are complete
            setTimeout(() => {
                try {
                    if (window.productsInventoryQuantity && variant) {
                        this.checkInventoryStatus(variant);
                    }
                } catch (error) {
                    console.error("Error during variant change:", error);
                } finally {
                    // Always reset the flag, even if there's an error
                    this.isChangingVariant = false;
                }
            }, 150); // Reduced from 300ms to 150ms
        }

        /**
         * Check inventory status and update button state
         * 
         * @param {object} variant Current variant data.
         * @returns {undefined}
         */
        async checkInventoryStatus(variant) {
            if (!window.productsInventoryQuantity || !variant) return;
            
            if (this.isChangingVariant) {
                console.log("Skipping inventory check during variant change");
                return;
            }
            
            try {
                // Get fresh cart data
                const req = await fetch(window.Shopify.routes.root + 'cart.js');
                const res = await req.json();
                const variantInCart = res.items.find(item => item.variant_id === variant.id);
                
                // Find the parent container (quickview or product page)
                const container = this.closest("component-quickview") || document;
                const btn = container.querySelector('.product-addtocart__button');
                const inStockItem = container.querySelector('.js-in-stock');
                const allAddedItem = container.querySelector('.js-in-stock-all-added');
                
                if (!btn) {
                    console.log("No button found");
                    return;
                }

                // If the variant does not track inventory, always allow adding to cart
                if (!variant.inventory_management) {
                    btn.removeAttribute('disabled');
                    btn.classList.remove('disabled');
                    if (allAddedItem) allAddedItem.classList.add('hidden');
                    if (inStockItem) inStockItem.classList.add('hidden');
                    return;
                }
                
                // First check if the variant is available at all
                if (!variant.available) {
                    // Disable the button for sold-out variants
                    btn.disabled = true;
                    btn.setAttribute('disabled', 'disabled');
                    btn.classList.add('disabled');
                    return;
                }
                
                // Enable the button by default for available variants
                btn.removeAttribute('disabled');
                btn.classList.remove('disabled');
                
                // Parse inventory data
                const resultArray = window.productsInventoryQuantity.map(item => {
                    const [key, value] = item.split(':');
                    return { [key]: parseInt(value) };
                });

                const productVariantQuantity = resultArray.find(obj => obj.hasOwnProperty(variant.id));
                
                if (!productVariantQuantity) {
                    console.log("No inventory data found for variant", variant.id);
                    return;
                }
                
                const inventoryQuantity = productVariantQuantity[variant.id];
                const variantInCartQty = variantInCart ? variantInCart.quantity : 0;
                
                // Check if all inventory is in cart
                if (variantInCartQty > 0 && variantInCartQty >= inventoryQuantity) {
                    console.log("All items in cart, disabling button");

                    btn.disabled = true;
                    btn.setAttribute('disabled', 'disabled');
                    btn.classList.add('disabled');
                    
                    if (allAddedItem) {
                        allAddedItem.classList.remove('hidden');
                    }
                } else if (inventoryQuantity > 0 && inventoryQuantity <= 10) {
                    // Show low stock warning
                    const remainingStock = inventoryQuantity - variantInCartQty;
                    
                    if (remainingStock > 0 && inStockItem) {
                        const inStockTitle = inStockItem.querySelector('.js-in-stock-title');
                        const inStockQuantity = inStockItem.querySelector('.js-in-stock-quantity');
                        
                        if (inStockTitle && inStockQuantity) {
                            inStockTitle.innerHTML = variant.option1;
                            inStockQuantity.innerHTML = remainingStock;
                            inStockItem.classList.remove('hidden');
                        }
                    }
                }
            } catch (error) {
                console.error('Error checking inventory status:', error);
            }
        }

        updateLineProperty(variant) {
            const propertyComparePrice = document.querySelector('[name="properties[_compareAtPrice]"]');
            const propertyLPROP = document.querySelector('[name="properties[_LPROP]"]');
            const difference = +variant.compare_at_price - variant.price;
			const percentDifference = Math.floor((difference * 100) / variant.compare_at_price);

            if (
                variant.compare_at_price !== null
                && variant.compare_at_price > variant.price
                && percentDifference >= 40
            ) {
                propertyLPROP.disabled = false;
            } else {
                propertyLPROP.disabled = true;
            }

            if (
                variant.compare_at_price !== null
                && variant.compare_at_price > variant.price
            ) {
                propertyComparePrice.disabled = false;
            } else {
                propertyComparePrice.disabled = true;
            }
        }

        /**
         * Get selected variant from swatch inputs.
         *
         * @param {object} product Product data.
         * @returns {object} Product variant data.
         */
        getSelectedVariant(product) {

            // Create object with selected options
            let selectedOptions = {};

            // Save the selected options in to the object
            this.querySelectorAll('[data-option]').forEach(element => {

                let input = element.querySelector('[data-swatch-input]:checked');

                if (input) {
                    selectedOptions['option'+element.dataset.position] = input.value;
                }
            });

            // First try to find exact match with all selected options
            let exactMatch = product.variants.find((item) => {
                return ((item.option1 == selectedOptions.option1) && (item.option2 == selectedOptions.option2) && (item.option3 == selectedOptions.option3))
            });

            if (exactMatch) {
                return exactMatch;
            }

            // If no exact match, try to find a variant that matches the color (option2) only
            if (selectedOptions.option2) {
                let colorMatch = product.variants.find((item) => {
                    return item.option2 == selectedOptions.option2;
                });
                
                if (colorMatch) {
                    return colorMatch;
                }
            }

            // Fallback: return first available variant or first variant
            return product.variants.find(v => v.available) || product.variants[0];
        }

        /**
         * Update variant selector web component.
         *
         * @param {object} variant Product variant data.
         * @returns {undefined}
         */
        updateVariantSelector(variant) {
          const currentUrl = window.location.href;

          if (!currentUrl.includes(variant.id) && currentUrl.includes("/products/")) {
            window.history.replaceState({}, '', `/products/${this.dataset.handle}?variant=${variant.id}`);
          }

          let variantSelector = document.querySelector('variant-selector[data-handle="' + this.dataset.handle + '"] select');

          if (variantSelector) {
            variantSelector.value = (variant) ? variant.id : '';
            const event = new CustomEvent("change", { bubbles: true, detail: { isSizeSelected: this.isSizeSelected } });
            variantSelector.dispatchEvent(event);
          }
        }

        /**
         * Get variant selector web component.
         *
         * @returns {object} Variant selector web component from DOM.
         */
        getVariantSelector() {
            return document.querySelector('variant-selector[data-handle="' + this.dataset.handle + '"] select');
        }

        /**
         * Update swatch option label with the current selected value.
         *
         * @param {object} input Swatch input.
         * @returns {undefined}
         */
        updateOptionLabel(input) {
            this.querySelector('[data-swatch-label="' + input.name + '"]').innerHTML  = input.value;
        }

        /**
         * Select the first variant available from a product in the variant selector component.
         *
         * @param {object} product Product data.
         * @returns {undefined}
         */

        getVariantParam(url) {
          const queryString = url.split('?')[1];

          if (!queryString) return null;

          const params = new URLSearchParams(queryString);

          if (params.has('variant')) {
            return params.get('variant');
          } else {
            return null;
          }
        }

        selectFirstVariantAvailable(product) {
          let currentProductUrl = window.location.href;
          let currentProductVariantId;
          let firstAvailableOption = document.querySelector('variant-selector[data-handle="'+this.dataset.handle+'"] option[data-enabled]');

          currentProductUrl = this.getVariantParam(currentProductUrl);

          if (!firstAvailableOption) firstAvailableOption = document.querySelector('variant-selector[data-handle="'+this.dataset.handle+'"] option');

          if (currentProductUrl) {
            currentProductVariantId = currentProductUrl;
          } else {
            if (firstAvailableOption) {
              currentProductVariantId = firstAvailableOption.dataset.variantId;
            }
          }

          if (currentProductVariantId) {
            let variant = product.variants.find(item => {return item.id == currentProductVariantId});
            // Only select the color (position 2), leave size unselected for user interaction
            this.selectColorOnly(variant);
          }
        }

        /**
         * Select variant from a product in the variant selector component.
         *
         * @todo Try to improve this code to reduce the code quantity.
         * @param {object} variant Product variant data.
         * @returns {undefined}
         */
        selectVariant(variant) {
            if (variant) {
                if(variant.option1) {
                    let option1 = variant.option1.trim().toLowerCase();
                    let inputOption1 = Array.from(this.querySelectorAll('[data-option][data-position="1"] input')).find(
                        input => input.value.trim().toLowerCase() === option1
                    );
                    if (inputOption1) {
                        if (!inputOption1.checked) {
                            inputOption1.checked = true;
                            inputOption1.dispatchEvent(new Event('change'));
                        }
                        this.updateOptionLabel(inputOption1);
                    }
                }
                if(variant.option2) {
                    let option2 = variant.option2.trim().toLowerCase();
                    let inputOption2 = Array.from(this.querySelectorAll('[data-option][data-position="2"] input')).find(
                        input => input.value.trim().toLowerCase() === option2
                    );
                    if (inputOption2) {
                        if (!inputOption2.checked) {
                            inputOption2.checked = true;
                            inputOption2.dispatchEvent(new Event('change'));
                        }
                        this.updateOptionLabel(inputOption2);
                    }
                }
                if(variant.option3) {
                    let option3 = variant.option3.trim().toLowerCase();
                    let inputOption3 = Array.from(this.querySelectorAll('[data-option][data-position="3"] input')).find(
                        input => input.value.trim().toLowerCase() === option3
                    );
                    if (inputOption3) {
                        if (!inputOption3.checked) {
                            inputOption3.checked = true;
                            inputOption3.dispatchEvent(new Event('change'));
                        }
                        this.updateOptionLabel(inputOption3);
                    }
                }
            }
        }

        /**
         * Select only the color option from a variant (for initialization).
         * This method only selects the color (position 2) and leaves size unselected.
         *
         * @param {object} variant Product variant data.
         * @returns {undefined}
         */
        selectColorOnly(variant) {
            if (variant && variant.option2) {
                let option2 = variant.option2.trim().toLowerCase();
                let inputOption2 = Array.from(this.querySelectorAll('[data-option][data-position="2"] input')).find(
                    input => input.value.trim().toLowerCase() === option2
                );
                if (inputOption2) {
                    if (!inputOption2.checked) {
                        inputOption2.checked = true;
                        inputOption2.dispatchEvent(new Event('change'));
                    }
                    this.updateOptionLabel(inputOption2);
                }
            }
        }

        /**
         * Disable out of stock variant swatches.
         *
         * @returns {undefined}
         */
        disableOutOfStockVariants() {
            const variantSelector = this.getVariantSelector();

            let selectedColor = null;
            const allOptions = this.querySelectorAll('[data-option]');
            for (let option of allOptions) {
                const optionName = option.getAttribute('data-option');
                if (optionName && (optionName.toLowerCase().includes('color') || optionName.toLowerCase().includes('colour'))) {
                    const colorInput = option.querySelector('input[data-swatch-input]:checked');
                    selectedColor = colorInput ? colorInput.value : null;
                    break;
                }
            }
        
            if (!selectedColor) return;
        
            for (let option of allOptions) {
                const optionName = option.getAttribute('data-option');
                if (optionName && optionName.toLowerCase().includes('size')) {
                    option.querySelectorAll('input[data-swatch-input]').forEach(sizeInput => {
                        const sizeValue = sizeInput.value.replaceAll(' ', '-');
                        const colorValue = selectedColor.replaceAll(' ', '-');
        
                        const selector = `[data-swatches-option-COLOR="${colorValue}"][data-swatches-option-SIZE="${sizeValue}"][data-enabled]`;
        
                        const matchingVariants = variantSelector.querySelectorAll(selector);
        
                        if (matchingVariants.length === 0) {
                            sizeInput.classList.add('disabled');
                        } else {
                            sizeInput.classList.remove('disabled');
                        }
                    });
                    break;
                }
            }
        }

        /**
         * Update swatch inputs.
         *
         * @returns {undefined}
         */
        updateSwatchValues() {

        }

        /**
         * Reset "All items have been added to your cart" message and button state
         */
        resetInventoryUI() {
            // Find the parent container (quickview or product page)
            const container = this.closest("component-quickview") || document;
            
            // Reset UI elements
            const inStockEl = container.querySelector('.js-in-stock');
            const allAddedEl = container.querySelector('.js-in-stock-all-added');
            const addToCartBtn = container.querySelector('.product-addtocart__button');
            
            if (inStockEl) inStockEl.classList.add('hidden');
            if (allAddedEl) allAddedEl.classList.add('hidden');
            
            if (addToCartBtn) {
                addToCartBtn.removeAttribute('disabled');
                addToCartBtn.classList.remove('disabled');
            }
        }
    }

    customElements.define(customElements.component, productAssociation);
}

